import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Label,
  CartesianGrid,
  Treemap,
} from 'recharts';
import type { AdvancedChartType, AggregatedRow } from './types';
import { formatMetric } from './utils';

type RowChartProps = {
  rows: AggregatedRow[];
  variant: AdvancedChartType;
  metricLabel: string;
  unit: string;
  loading?: boolean;
  onSelectRow?: (row: AggregatedRow) => void;
  isRowSelected?: (row: AggregatedRow) => boolean;
  isPanelActive?: boolean;
  chartRef?: React.RefObject<HTMLDivElement | null>;
};

const COLOR_PALETTE = ['#1f4bd6', '#e53935', '#f59e0b', '#7c3aed', '#10b981', '#64748b'];

function TreemapCell(props: Record<string, unknown>) {
  const x = Number(props.x ?? 0);
  const y = Number(props.y ?? 0);
  const width = Number(props.width ?? 0);
  const height = Number(props.height ?? 0);
  const name = typeof props.name === 'string' ? props.name : '';
  const fill = typeof props.fill === 'string' ? props.fill : COLOR_PALETTE[0];

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke="#fff"
        strokeWidth={2}
        rx={4}
      />
      {width > 40 && height > 20 ? (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontSize={Math.min(11, width / 8)}
          fontWeight={600}
        >
          {truncate(name, Math.floor(width / 7))}
        </text>
      ) : null}
    </g>
  );
}

const truncate = (value: string, length: number) =>
  value.length > length ? `${value.slice(0, length - 1)}...` : value;

export function RowChart({
  rows,
  variant,
  metricLabel,
  unit,
  loading,
  onSelectRow,
  isRowSelected,
  isPanelActive,
  chartRef,
}: RowChartProps) {
  if (loading) {
    return (
      <div className="flex h-72 items-center justify-center text-sm text-gray-500">
        <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        <span className="ml-2">Cargando...</span>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="flex h-72 items-center justify-center text-sm text-gray-500">Sin datos</div>
    );
  }

  const totalMetric = rows.reduce((sum, row) => sum + row.metricValue, 0);

  const wrapChart = (content: React.ReactNode) => <div ref={chartRef}>{content}</div>;

  const chartData = rows.map((row, index) => {
    const selected = isRowSelected?.(row) ?? false;
    const fill = selected
      ? COLOR_PALETTE[0]
      : isPanelActive
        ? COLOR_PALETTE[5]
        : COLOR_PALETTE[index % COLOR_PALETTE.length];
    const opacity = selected ? 1 : isPanelActive ? 0.35 : 0.9;

    return {
      ...row,
      fill,
      opacity,
    };
  });

  if (variant === 'treemap') {
    const treemapData = chartData.map((row, index) => ({
      name: truncate(row.label, 20),
      size: row.metricValue,
      fill: row.fill,
      fullLabel: row.label,
      secondaryLabel: row.secondaryLabel,
      index,
    }));

    return wrapChart(
      <div className="flex h-72 items-center">
        <div className="h-full w-full">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={treemapData}
              dataKey="size"
              nameKey="name"
              stroke="#fff"
              onClick={(node: { index?: number }) => {
                const idx = node?.index;
                if (typeof idx === 'number' && rows[idx]) onSelectRow?.(rows[idx]);
              }}
              style={{ cursor: onSelectRow ? 'pointer' : 'default' }}
              content={<TreemapCell />}
            >
              <Tooltip
                formatter={(value: number) => [`${formatMetric(value)} ${unit}`, metricLabel]}
                labelFormatter={(_, payload) => {
                  const node = payload?.[0]?.payload as
                    | { fullLabel?: string; secondaryLabel?: string }
                    | undefined;
                  if (!node) return '';
                  return node.secondaryLabel
                    ? `${node.fullLabel} (${node.secondaryLabel})`
                    : (node.fullLabel ?? '');
                }}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                  fontSize: 10,
                }}
                labelStyle={{ fontSize: 10 }}
                itemStyle={{ fontSize: 10 }}
                wrapperStyle={{ maxWidth: '90vw' }}
              />
            </Treemap>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (variant === 'pie') {
    return wrapChart(
      <div className="flex h-72 items-center">
        <div className="h-full w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="metricValue"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={62}
                outerRadius={92}
                paddingAngle={2}
                onClick={(_, index) => {
                  const item = rows[index];
                  if (item) onSelectRow?.(item);
                }}
                style={{ cursor: onSelectRow ? 'pointer' : 'default' }}
              >
                {chartData.map(item => (
                  <Cell key={item.key} fill={item.fill} fillOpacity={item.opacity} />
                ))}
                <Label
                  value="TOTAL"
                  position="center"
                  dy={-10}
                  className="fill-gray-500 text-[10px] font-semibold"
                />
                <Label
                  value={formatMetric(totalMetric)}
                  position="center"
                  dy={10}
                  className="fill-gray-900 text-sm font-bold"
                />
              </Pie>
              <Tooltip
                formatter={(value: number) => [`${formatMetric(value)} ${unit}`, metricLabel]}
                labelFormatter={(_, payload) => {
                  const row = payload?.[0]?.payload as AggregatedRow | undefined;
                  if (!row) return '';
                  return row.secondaryLabel ? `${row.label} (${row.secondaryLabel})` : row.label;
                }}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                  fontSize: 10,
                }}
                labelStyle={{ fontSize: 10 }}
                itemStyle={{ fontSize: 10 }}
                wrapperStyle={{ maxWidth: '90vw' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (variant === 'bar-vertical') {
    return wrapChart(
      <div className="flex h-72 items-center">
        <div className="h-full w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 18, left: 6, bottom: 44 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                height={58}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickFormatter={(value: string) => truncate(String(value), 12)}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickFormatter={(value: number) => formatMetric(value)}
              />
              <Tooltip
                formatter={(value: number) => [`${formatMetric(value)} ${unit}`, metricLabel]}
                labelFormatter={(label, payload) => {
                  const row = payload?.[0]?.payload as AggregatedRow | undefined;
                  if (!row) return String(label);
                  return row.secondaryLabel ? `${row.label} (${row.secondaryLabel})` : row.label;
                }}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                  fontSize: 10,
                }}
                labelStyle={{ fontSize: 10 }}
                itemStyle={{ fontSize: 10 }}
                wrapperStyle={{ maxWidth: '90vw' }}
              />
              <Bar dataKey="metricValue" radius={[4, 4, 0, 0]}>
                {chartData.map(row => (
                  <Cell
                    key={row.key}
                    fill={row.fill}
                    fillOpacity={row.opacity}
                    onClick={() => onSelectRow?.(row)}
                    style={{ cursor: onSelectRow ? 'pointer' : 'default' }}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return wrapChart(
    <div className="flex h-72 items-center">
      <div className="h-full w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 8, right: 18, left: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: '#6b7280' }}
              tickFormatter={(value: number) => formatMetric(value)}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={120}
              tick={{ fontSize: 10, fill: '#6b7280' }}
              tickFormatter={(value: string) => truncate(String(value), 16)}
            />
            <Tooltip
              formatter={(value: number) => [`${formatMetric(value)} ${unit}`, metricLabel]}
              labelFormatter={(label, payload) => {
                const row = payload?.[0]?.payload as AggregatedRow | undefined;
                if (!row) return String(label);
                return row.secondaryLabel ? `${row.label} (${row.secondaryLabel})` : row.label;
              }}
              contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                fontSize: 10,
              }}
              labelStyle={{ fontSize: 10 }}
              itemStyle={{ fontSize: 10 }}
              wrapperStyle={{ maxWidth: '90vw' }}
            />
            <Bar dataKey="metricValue" radius={[0, 4, 4, 0]}>
              {chartData.map(row => (
                <Cell
                  key={row.key}
                  fill={row.fill}
                  fillOpacity={row.opacity}
                  onClick={() => onSelectRow?.(row)}
                  style={{ cursor: onSelectRow ? 'pointer' : 'default' }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
